import { useCallback, useEffect, useMemo, useRef } from 'react';

import { actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { useStatusContext } from 'pages/Status/context';

interface NovunaSignDocumentsHook {
  isLoading: boolean;
  link?: string;
}

export default function useNovunaSignDocuments(): NovunaSignDocumentsHook {
  const { isLoading: creditIsLoading, credit } = useStatusContext();
  const dispatch = useAppDispatch();
  const onError = useApiError('Error checking credit - please refresh');

  const xref = useMemo(() => {
    if (!credit) return undefined;
    return credit?.xref;
  }, [credit]);

  const isLoading = creditIsLoading || !xref;

  const checkCreditAndRevalidate = useCallback(
    (id: string) => {
      if (!id) return;
      dispatch(
        actions.checkCredit({
          params: { id },
          onError,
          onSuccess: () => {
            actions.getCredit({
              params: { id },
              forceReload: true,
            });
          },
        }),
      );
    },
    [dispatch, onError],
  );

  // Reference for autoredirect timer
  const timerRef = useRef<NodeJS.Timer | undefined>();
  // Function for autoredirect
  const startTimeout = useCallback((url: string) => {
    clearTimeout(timerRef.current as NodeJS.Timeout);
    timerRef.current = setTimeout(() => {
      window.location.href = url;
    }, 10000);
  }, []);

  const creditIsCheckedRef = useRef<boolean>(false);
  useEffect(() => {
    if (!credit || xref || creditIsCheckedRef.current) return;
    const id = credit?.id;
    if (!id) return;
    creditIsCheckedRef.current = true;
    checkCreditAndRevalidate(id);
  }, [credit, xref, checkCreditAndRevalidate]);

  // Effect to call autoredirect
  useEffect(() => {
    if (!xref) return () => {};
    if (!timerRef.current) startTimeout(xref);
    return () => clearTimeout(timerRef.current as NodeJS.Timeout);
  }, [xref, startTimeout]);

  return {
    isLoading,
    link: xref,
  };
}
